import React, { useState, useMemo, useEffect, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faEdit,
  faTrash,
  faUserGraduate,
  faTimes,
  faEye,
  faUserCheck,
  faUserSlash,
  faEnvelope,
  faPhone,
  faGraduationCap,
  faBook,
  faCheckCircle,
  faExclamationCircle,
  faShare,
} from "@fortawesome/free-solid-svg-icons";
import { motion, AnimatePresence } from "framer-motion";
import * as Yup from "yup";
import Loader from "../../Shared/Loader";
import ApiServices from "../../Services/ApiServices";
import { toastHelper } from "../../../Helpers/toastHelper";
import WarningAlert from "../../Shared/WarningAlert";
import DeleteAlert from "../../Shared/DeleteAlert";
import { useNavigate } from "react-router-dom";
import SoftGrid from "../../Shared/SoftGrid/SoftGrid";
import { extractHost, getFullFileUrl } from "../../../Helpers/UrlHelpers";
import { storageHelper } from "../../../Helpers/storageHelper";
import { encryptData } from "../../../utils/encryption";
import extractErrorMessage from "../../../Helpers/APIHelper";
import DynamicModal from "../../Shared/DynamicModal";
import { useLanguage } from "../../../contexts/LanguageContext";
import ShareQrData from "../../Shared/ShareQrData";



const EMPTY_STUDENT_FORM = {
  fullName: "",
  email: "",
  user_class: "",
  material_id: "",
  gender: "",
  phone: "",
  password: "",
  profile_picture: null,
};

const StudentManager = () => {
  const { t } = useLanguage();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);

  const genderOptions = useMemo(() => [
    { value: "1", label: t("male") },
    { value: "2", label: t("female") },
  ], [t]);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [totalRecords, setTotalRecords] = useState(0);
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(null);

  const [selectedImage, setSelectedImage] = useState(null);
  const [studentToDeactivate, setStudentToDeactivate] = useState(null);
  const [showStudentModal, setShowStudentModal] = useState(false);

  const handleShareStudent = (student) => {
    const studentInfo = {
      studentId: student.id,
      fullName: student.full_name,
      email: student.email,
      phone: student.phone_number,
      userClass: student.user_class_name,
      material: student.material,
    };
    const encryptedData = encryptData(studentInfo);
    const params = new URLSearchParams({
      info: encryptedData,
      title: `مشاركة بيانات الطالب: ${student.full_name}`,
      subtitle: "رمز QR مشفر يتضمن بيانات الطالب المعتمدة لدى المنصة",
    });
    window.open(`/share-qr?${params.toString()}`, "_blank");
  };
  const [studentModalLoading, setStudentModalLoading] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferModalLoading, setTransferModalLoading] = useState(false);
  const [transferValues, setTransferValues] = useState(null);
  const [isTransferConfirmLoading, setIsTransferConfirmLoading] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null);
  const [studentFormInitialData, setStudentFormInitialData] = useState(EMPTY_STUDENT_FORM);
  const [lookupsLoaded, setLookupsLoaded] = useState(false);
  const [lookupsLoading, setLookupsLoading] = useState(false);
  const [userClasses, setUserClasses] = useState([]);
  const [materials, setMaterials] = useState([]);
  const navigate = useNavigate();

  const fetchStudents = useCallback(async (page, searchWord = "") => {
    try {
      setLoading(true);
      const response = await ApiServices.get(
        `Dashboard/GetAllUserForTeacher?PageSize=${pageSize}&PageNumber=${page}&IsActive=true&Word=${encodeURIComponent(
          searchWord
        )}`
      );

      if (response.check) {
        setStudents(response.data.items);
        setTotalPages(response.data.total_pages);
        setTotalRecords(response.data.total_records);
      }
    } catch (err) {
      toastHelper.error(t("dataLoadError"));
    } finally {
      setLoading(false);
    }
  }, [pageSize, t]);

  useEffect(() => {
    fetchStudents(currentPage, searchQuery);
  }, [currentPage, searchQuery, fetchStudents]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const closeStudentModal = () => {
    setShowStudentModal(false);
    setEditingStudent(null);
    setStudentFormInitialData(EMPTY_STUDENT_FORM);
  };

  const loadStudentLookups = async () => {
    if (lookupsLoaded) return;

    try {
      setLookupsLoading(true);
      const response = await ApiServices.get(
        "Dashboard/GetAllUserForTeacher?PageSize=1&PageNumber=1&IsActive=true"
      );
      if (response?.check) {
        setUserClasses(response?.look_ups?.user_classess || []);
        setMaterials(response?.look_ups?.materials || []);
        setLookupsLoaded(true);
      }
    } catch (error) {
      toastHelper.error(t("studentLookupsLoadError"));
      throw error;
    } finally {
      setLookupsLoading(false);
    }
  };

  const openCreateStudentModal = async () => {
    try {
      setStudentModalLoading(true);
      await loadStudentLookups();
      setEditingStudent(null);
      setStudentFormInitialData(EMPTY_STUDENT_FORM);
      setShowStudentModal(true);
    } finally {
      setStudentModalLoading(false);
    }
  };

  const openTransferModal = async () => {
    try {
      setTransferModalLoading(true);
      await loadStudentLookups();
      setShowTransferModal(true);
    } finally {
      setTransferModalLoading(false);
    }
  };

  const handleTransferClassSubmit = async (values) => {
    setTransferValues(values);
    setShowTransferModal(false);
  };

  const confirmTransferClass = async () => {
    if (!transferValues) return;

    setIsTransferConfirmLoading(true);
    try {
      const response = await ApiServices.put(
        `Dashboard/User/AssignStudentClassToAnotherClass?oldClassId=${transferValues.oldClassId}&newClassId=${transferValues.newClassId}`
      );
      if (response?.check) {
        toastHelper.success(t("classTransferSuccess") || "تم نقل الطلاب بنجاح");
        fetchStudents(currentPage, searchQuery);
        setTransferValues(null);
      } else {
        throw new Error(response?.msg || "فشل نقل الطلاب");
      }
    } catch (error) {
      toastHelper.error(error?.message || t("classTransferError") || "حدث خطأ أثناء نقل الطلاب");
    } finally {
      setIsTransferConfirmLoading(false);
    }
  };

  const openEditStudentModal = async (student) => {
    try {
      setStudentModalLoading(true);
      await loadStudentLookups();

      const response = await ApiServices.get(`Dashboard/StudentProfile/${student.id}`);
      if (!response?.check) {
        toastHelper.error(t("studentNotFound"));
        return;
      }

      const data = response.data || {};
      setEditingStudent({ ...student, ...data });
      setStudentFormInitialData({
        fullName: data.full_name || "",
        email: data.email || "",
        user_class:
          data.user_class !== null && data.user_class !== undefined
            ? String(data.user_class)
            : "",
        material_id:
          data.material_id !== null && data.material_id !== undefined
            ? String(data.material_id)
            : "",
        gender: data.gender !== null && data.gender !== undefined ? String(data.gender) : "1",
        phone: data.phone_number || "",
        password: "",
        profile_picture: null,
      });
      setShowStudentModal(true);
    } catch (error) {
      toastHelper.error(error?.msg || t("studentProfileLoadError"));
    } finally {
      setStudentModalLoading(false);
    }
  };

  const handleDelete = (student) => {
    setSelectedStudent(student);
    setShowDeleteAlert(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedStudent) return;

    setDeleteLoading(true);
    try {
      const response = await ApiServices.delete(
        `User/DeleteUser/${selectedStudent.id}`
      );
      if (response.check) {
        toastHelper.success(t("studentDeleteSuccess"));
        fetchStudents(currentPage, searchQuery);
      }
    } catch (error) {
      toastHelper.error(t("studentDeleteError"));
    } finally {
      setDeleteLoading(false);
      setShowDeleteAlert(false);
      setSelectedStudent(null);
    }
  };

  const handleStatusChange = async (studentId, newStatus) => {
    setStatusLoading(studentId);
    try {
      await ApiServices.put(`Dashboard/User/Active/${studentId}`);
      fetchStudents(currentPage, searchQuery);
      toastHelper.success(
        newStatus ? t("studentActivateSuccess") : t("studentDeactivateSuccess")
      );
    } catch (error) {
      toastHelper.error(t("studentStatusError"));
    } finally {
      setStatusLoading(null);
    }
  };



  const closeImagePreview = () => {
    setSelectedImage(null);
  };

  const handleSearch = (term) => {
    setSearchQuery(term);
    setCurrentPage(1);
  };

  const transferFormConfig = useMemo(() => {
    const classOptions = (userClasses || []).map((cls) => ({
      value: String(cls.id),
      label: cls.name,
    }));

    return [
      {
        name: "oldClassId",
        label: t("oldClass"),
        type: "select",
        required: true,
        requiredMessage: t("classRequired"),
        options: classOptions,
        defaultValue: "",
        validation: Yup.string().required(t("classRequired")),
      },
      {
        name: "newClassId",
        label: t("newClass"),
        type: "select",
        required: true,
        requiredMessage: t("classRequired"),
        options: classOptions,
        defaultValue: "",
        validation: Yup.string()
          .required(t("classRequired"))
          .test('not-same', t("classesMustBeDifferent"), function (value) {
            return value !== this.parent.oldClassId;
          }),
      },
    ];
  }, [userClasses, t]);

  const studentFormConfig = useMemo(() => {
    const classOptions = (userClasses || []).map((cls) => ({
      value: String(cls.id),
      label: cls.name,
    }));
    const materialOptions = (materials || []).map((item) => ({
      value: String(item.id),
      label: item.name,
    }));

    if (
      studentFormInitialData.user_class &&
      !classOptions.some((opt) => opt.value === String(studentFormInitialData.user_class))
    ) {
      classOptions.unshift({ value: String(studentFormInitialData.user_class), label: t("currentClass") });
    }

    if (
      studentFormInitialData.material_id &&
      !materialOptions.some((opt) => opt.value === String(studentFormInitialData.material_id))
    ) {
      materialOptions.unshift({ value: String(studentFormInitialData.material_id), label: t("currentMaterial") });
    }

    return [
      {
        name: "fullName",
        label: t("studentName"),
        type: "text",
        required: true,
        defaultValue: studentFormInitialData.fullName,
        validation: Yup.string()
          .matches(/^[\u0621-\u064Aa-zA-Z\s]+$/, t("nameLettersOnly"))
          .required(t("studentNameRequired")),
      },
      {
        name: "email",
        label: t("email"),
        type: "email",
        required: true,
        requiredMessage: t("emailRequired"),
        defaultValue: studentFormInitialData.email,
      },
      {
        name: "user_class",
        label: t("class"),
        type: "select",
        required: true,
        requiredMessage: t("classRequired"),
        options: classOptions,
        defaultValue: studentFormInitialData.user_class,
      },
      {
        name: "material_id",
        label: t("materialLabel"),
        type: "select",
        required: true,
        requiredMessage: t("materialRequired"),
        options: materialOptions,
        defaultValue: studentFormInitialData.material_id,
      },
      {
        name: "gender",
        label: t("gender"),
        type: "select",
        required: true,
        requiredMessage: t("genderRequired"),
        options: genderOptions,
        defaultValue: studentFormInitialData.gender,
      },
      {
        name: "phone",
        label: t("phone"),
        type: "tel",
        required: true,
        requiredMessage: t("phoneRequired"),
        defaultValue: studentFormInitialData.phone,
        validation: Yup.string()
          .matches(/^01[0-9]{9}$/, t("invalidPhoneFormat"))
          .required(t("phoneRequired")),
      },
      {
        name: "password",
        label: t("passwordOptional"),
        type: "password",
        required: false,
        min: 6,
        minMessage: t("passwordMinLengthHint"),
        defaultValue: "",
        validation: Yup.string()
          .nullable()
          .transform((value) => (value === '' ? null : value))
          .min(6, t("passwordMinLengthHint")),
      },
      {
        name: "profile_picture",
        label: t("studentImageOptional"),
        type: "file",
        accept: "image/*",
        defaultValue: null,
      },
    ];
  }, [materials, userClasses, studentFormInitialData, genderOptions, t]);

  const handleStudentModalSubmit = async (values) => {
    const formData = new FormData();
    formData.append("fullName", values.fullName || "");
    formData.append("user_class", values.user_class || "");
    formData.append("material_id", values.material_id || "");
    formData.append("gender", values.gender || "");
    formData.append("email", values.email || "");
    formData.append("phone", values.phone || "");

    if (values.password) {
      formData.append("password", values.password);
    }

    if (values.profile_picture instanceof File) {
      formData.append("profile_picture", values.profile_picture);
    }

    try {
      let response;
      if (editingStudent?.id) {
        response = await ApiServices.put(`User/UpdateUser/${editingStudent.id}`, formData, true);
        if (!response?.check) throw new Error(t("studentUpdateError"));
        toastHelper.success(t("studentUpdateSuccess"));
      } else {
        const currentTeacherId = storageHelper.getItem("user_id");
        if (currentTeacherId) {
          formData.append("teacher_id", currentTeacherId);
        }
        response = await ApiServices.post("User/Add", formData, true);
        if (!response?.check) throw new Error(t("studentAddFailed"));
        toastHelper.success(t("studentAddSuccess"));
      }

      closeStudentModal();
      await fetchStudents(currentPage, searchQuery);
    } catch (error) {
      const title = error?.title || error?.message || "";
      if (title.toLowerCase().includes("validation errors")) {
        toastHelper.error(extractErrorMessage(error));
      } else {
        toastHelper.error(error?.msg || error?.message || t("studentSaveError"));
      }
      throw error;
    }
  };

  if (loading) {
    return <Loader text={t("studentLoading")} />;
  }

  return (
    <div className="student-manager">
      <div className="section-header">
        <div className="header-left">
          <h2>{t("studentManagement")}</h2>
        </div>
        <div className="header-right" style={{ display: 'flex', gap: '8px' }}>
          <button
            className="add-student-btn transfer-btn"
            onClick={openTransferModal}
            disabled={transferModalLoading || lookupsLoading}
          >
            <FontAwesomeIcon icon={faShare} />
            {transferModalLoading || lookupsLoading ? t("loading") : t("transferClass")}
          </button>
          <button
            className="add-student-btn"
            onClick={openCreateStudentModal}
            disabled={studentModalLoading || lookupsLoading}
          >
            <FontAwesomeIcon icon={faPlus} />
            {studentModalLoading || lookupsLoading ? t("loading") : t("addNewStudent")}
          </button>
        </div>
      </div>

      <DynamicModal
        isOpen={showStudentModal}
        onClose={closeStudentModal}
        title={editingStudent?.id ? t("editStudentProfile") : t("addNewStudent")}
        submitButtonText={editingStudent?.id ? t("updateStudentData") : t("addStudentBtn")}
        formConfig={studentFormConfig}
        onSubmit={handleStudentModalSubmit}
      />

      <DynamicModal
        isOpen={showTransferModal}
        onClose={() => setShowTransferModal(false)}
        title={t("transferClassTitle")}
        submitButtonText={t("transferClass")}
        formConfig={transferFormConfig}
        onSubmit={handleTransferClassSubmit}
      />

      <WarningAlert
        isOpen={!!transferValues}
        onClose={() => setTransferValues(null)}
        onConfirm={confirmTransferClass}
        title={t("transferWarningTitle")}
        message={t("transferWarningMessage")}
        warningText={t("transferWarningAttention")}
        confirmText={t("transferClass")}
        cancelText={t("cancel")}
        loading={isTransferConfirmLoading}
      />

      <DeleteAlert
        isOpen={showDeleteAlert}
        onClose={() => {
          setShowDeleteAlert(false);
          setSelectedStudent(null);
        }}
        onConfirm={handleDeleteConfirm}
        title={t("confirmStudentDeleteTitle")}
        message={t("confirmStudentDeleteMessage")}
        itemName={selectedStudent?.full_name}
        itemDescription={`${t("emailLabelPrefix")}${selectedStudent?.email}`}
        loading={deleteLoading}
      />

      {/* Image Preview Modal */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            className="image-preview-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeImagePreview}
          >
            <motion.div
              className="image-preview-modal"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="image-preview-modal-header">
                <h3>{selectedImage.full_name}</h3>
                <button className="close-modal-btn" onClick={closeImagePreview}>
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
              <div className="image-preview-modal-content">
                <img
                  src={getFullFileUrl(selectedImage.profile_picture)}
                  alt={`${selectedImage.full_name} profile`}
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>


      <SoftGrid
        columns={[
          {
            key: "id",
            label: t("studentIdLabel"),
            sortable: true,
            render: (value, row) => (
              <div className="exam-title-cell">
                <span>{value}</span>
              </div>
            ),
          },
          {
            key: "full_name",
            label: t("studentName"),
            sortable: true,
            render: (value, row) => (
              <div className="student-name-cell">
                <FontAwesomeIcon icon={faUserGraduate} />
                <span>{value}</span>
              </div>
            ),
          },
          {
            key: "email",
            label: t("email"),
            sortable: true,
            render: (value) => (
              <div className="email-cell">
                <FontAwesomeIcon icon={faEnvelope} />
                <span>{value || t("notSpecified")}</span>
              </div>
            ),
          },
          {
            key: "phone_number",
            label: t("phone"),
            sortable: true,
            render: (value) => (
              <div className="phone-cell">
                <FontAwesomeIcon icon={faPhone} />
                <span>{value || t("notSpecified")}</span>
              </div>
            ),
          },
          {
            key: "user_class_name",
            label: t("class"),
            sortable: true,
            render: (value) => (
              <div className="class-cell">
                <FontAwesomeIcon icon={faGraduationCap} />
                <span>{value === "-" ? t("notSpecified") : value}</span>
              </div>
            ),
          },
          {
            key: "material",
            label: t("materialLabel"),
            sortable: true,
            render: (value) => (
              <div className="material-cell">
                <FontAwesomeIcon icon={faBook} />
                <span>{value === "-" ? t("notSpecified") : value}</span>
              </div>
            ),
          },
          //   {
          //     key: 'gender',
          //     label: 'الجنس',
          //     render: (value) => (
          //       <div className="gender-cell">
          //         <FontAwesomeIcon icon={value === 1 ? faMale : faFemale} />
          //         <span>{value === 1 ? "ذكر" : "أنثى"}</span>
          //       </div>
          //     ),
          //   },
          {
            key: "is_active",
            label: t("statusLabel"),
            render: (value, row) => (
              <div className="status-container">
                <span
                  className={`status-badge ${value ? "active" : "inactive"}`}
                >
                  {value ? (
                    <>
                      <FontAwesomeIcon icon={faCheckCircle} />
                      {t("active")}
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faExclamationCircle} />
                      {t("inactive")}
                    </>
                  )}
                </span>
              </div>
            ),
          },
        ]}
        data={students}
        actions={[
          (row) => {
            const student = row;
            return [
              {
                tooltip: t("viewTooltip"),
                icon: <FontAwesomeIcon icon={faEye} />,
                onClick: () => navigate(`/teacher/students/view/${student.id}`),
              },
              {
                tooltip: t("edit"),
                icon: <FontAwesomeIcon icon={faEdit} />,
                onClick: () => openEditStudentModal(student),
              },
              {
                tooltip: t("shareStudentInfoTooltip"),
                icon: <FontAwesomeIcon icon={faShare} />,
                onClick: () => handleShareStudent(student),
                className: "share",
              },
              {
                tooltip: student.is_active ? t("deactivateTooltip") : t("activateTooltip"),
                icon: (
                  <FontAwesomeIcon
                    icon={student.is_active ? faUserSlash : faUserCheck}
                  />
                ),
                onClick: () => {
                  if (student.is_active) {
                    setStudentToDeactivate(student);
                  } else {
                    handleStatusChange(student.id, true);
                  }
                },
                className: student.is_active ? "warning" : "success",
                disabled: statusLoading === student.id,
              },
              {
                tooltip: t("delete"),
                icon: <FontAwesomeIcon icon={faTrash} />,
                onClick: () => handleDelete(student),
                className: "delete",
              },
            ];
          },
        ]}
        isLoading={loading}
        isPaginated={true}
        pageNumber={currentPage}
        pageSize={pageSize}
        totalPages={totalPages}
        totalRecords={totalRecords}
        onPageChange={handlePageChange}
        onPageSizeChange={(newSize) => {
          setPageSize(newPageSize => {
            if (newPageSize !== newSize) {
              setCurrentPage(1);
              return newSize;
            }
            return newPageSize;
          });
        }}
        searchPlaceholder={t("searchStudentsPlaceholder")}
        columnResizable={true}
        onSearch={handleSearch}
        isColumnDraggable={true}
        isColumnSortable={true}
        isColumnSelectable={true}
        emptyStateMessage={t("noStudentsAvailable")}
        useGroupBy={true}
        multiSelect={true}
        multiSelectAPI="User/DeleteStudentTeacher"
        onMultiSelectDeleteSuccess={() => fetchStudents(currentPage, searchQuery)}
      />



      <WarningAlert
        isOpen={!!studentToDeactivate}
        onClose={() => setStudentToDeactivate(null)}
        onConfirm={() => {
          if (studentToDeactivate) {
            handleStatusChange(studentToDeactivate.id, false);
            setStudentToDeactivate(null);
          }
        }}
        title={t("confirmStatusChangeTitle")}
        itemName={studentToDeactivate?.full_name}
        itemDescription={`${t("emailLabelPrefix")}${studentToDeactivate?.email}`}
        warningText={t("statusChangeWarningText")}
        confirmText={t("confirm")}
        cancelText={t("cancel")}
      />

      <style jsx>{`
        .student-manager {
          padding: 24px;
          background: #fff;
          min-height: calc(100vh - 48px);
          width: 100%;
          border-radius: 30px;
        }

        .section-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 32px;
          padding: 24px;
          background: rgba(255, 255, 255, 0.95);
          border-radius: 16px;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.2);
          backdrop-filter: blur(8px);
        }

        .header-left h2 {
          margin: 0;
          font-size: 24px;
          font-weight: 700;
          color: #1a1a1a;
        }

        .add-student-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 20px;
          background: #6366f1;
          color: white;
          border: none;
          border-radius: 12px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
            font-family: "Zain", sans-serif;
        }

        .add-student-btn:hover {
          background: #357abd;
        }

        .transfer-btn {
          background: #10b981 !important;
          box-shadow: 0 4px 6px -1px rgba(16, 185, 129, 0.3) !important;
        }

        .transfer-btn:hover {
          background: #059669 !important;
          transform: translateY(-1px);
        }

        .student-name-cell,
        .email-cell,
        .phone-cell,
        .class-cell,
        .material-cell,
        .gender-cell {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .student-image-cell {
          width: 60px;
        }

        .image-preview-container {
          display: flex;
          justify-content: center;
          align-items: center;
        }

        .student-thumbnail {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          object-fit: cover;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .no-image-placeholder {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: #f8fafc;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #64748b;
        }

        .status-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 6px 12px;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 500;
        }

        .status-badge.active {
          background: #f0fdf4;
          color: #10b981;
        }

        .status-badge.inactive {
          background: #fee2e2;
          color: #ef4444;
        }

        .image-preview-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.75);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
          padding: 20px;
        }

        .image-preview-modal {
          background: white;
          border-radius: 16px;
          max-width: 90vw;
          max-height: 90vh;
          overflow: hidden;
        }

        .image-preview-modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 24px;
          border-bottom: 1px solid #e2e8f0;
        }

        .image-preview-modal-header h3 {
          margin: 0;
          font-size: 18px;
          font-weight: 600;
        }

        .close-modal-btn {
          background: none;
          border: none;
          font-size: 20px;
          cursor: pointer;
          color: #64748b;
          padding: 4px;
          border-radius: 4px;
          transition: all 0.2s ease;
        }

        .close-modal-btn:hover {
          background: #f1f5f9;
          color: #1a1a1a;
        }

        .image-preview-modal-content {
          padding: 24px;
          display: flex;
          justify-content: center;
        }

        .image-preview-modal-content img {
          max-width: 100%;
          max-height: 70vh;
          border-radius: 8px;
        }

        @media (max-width: 768px) {
          .student-manager {
            padding: 16px;
          }

          .section-header {
            flex-direction: column;
            gap: 16px;
            padding: 16px;
          }

          .add-student-btn {
            width: 100%;
            justify-content: center;
          }
        }
      `}</style>
    </div>
  );
};

export default StudentManager;
